import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import FormButton from '@components/form/form-button';
import { useFormStore } from '@store/use-form-store';

import { ROUTES } from '../../constants/routes';
import {
  useCreateVisitors,
  useVismaConfiguration
} from '../../services/visitor-management';
import type { Registrant } from '../../types';
import RegistrantVideo from './registrant-video';
import VideoConfirmWarning from './video-confirm-warning';

const Container = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  overflow: auto;
`;

const RegisteredVisitor = styled.h3`
  margin-top: 74px;
  margin-left: 20px;
  font-size: 30px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;
const DescText = styled.h3`
  margin-left: 20px;
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};
  padding-right: 12px;
`;
const VisitorsContainer = styled.div`
  padding: 40px;
  height: 100%;
`;

const ButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const VideoWarn = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};
  text-align: end;
  width: 100%;
`;

const RegistrantsContainer = styled.div`
  max-height: 55vh;
  overflow: auto;
`;

interface AcceptWarningModal {
  status: boolean;
  registrants: Registrant[];
}

function MultipleRegistrationBanner() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { registeredPersons, isVideoWatched, resetProcess } = useFormStore();
  const { configuration } = useVismaConfiguration();
  const multi = configuration?.elearning?.multi;
  const [acceptWarningModal, setAcceptWarningModal] =
    useState<AcceptWarningModal>({
      status: false,
      registrants: []
    });

  const { mutateAsync: createVisitors, isPending: loading } =
    useCreateVisitors();

  const handleApi = async () => {
    try {
      if (loading) return;
      setAcceptWarningModal({ status: false, registrants: [] });
      const accepted = registeredPersons.filter(
        (item) => item.videoStatus === 'accept'
      );
      await createVisitors(accepted);
      navigate(ROUTES.VR_FINAL);
      resetProcess();
    } catch (e) {}
  };

  const handleProcessStep = () => {
    if (loading) return;
    let checkRejectedUsers = registeredPersons.filter(
      (item) => item.videoStatus !== 'accept' || !item.videoStatus
    );
    if (checkRejectedUsers.length) {
      setAcceptWarningModal({
        status: true,
        registrants: checkRejectedUsers
      });
    } else {
      handleApi();
    }
  };

  const handlePrevious = () => {
    navigate(ROUTES.VR_REGISTER);
  };

  return (
    <Container>
      <VideoConfirmWarning
        open={acceptWarningModal.status}
        closeModal={() =>
          setAcceptWarningModal({ status: false, registrants: [] })
        }
        registrants={acceptWarningModal.registrants ?? []}
        onConfirm={handleApi}
      />
      <RegisteredVisitor>{multi?.visitor_list_title}</RegisteredVisitor>
      <DescText>{multi?.visitor_list_description}</DescText>
      <VisitorsContainer>
        <>
          <RegistrantsContainer>
            {registeredPersons.map((registrant, index) => (
              <RegistrantVideo key={index} registrant={registrant} />
            ))}
          </RegistrantsContainer>

          {!isVideoWatched && (
            <VideoWarn>
              {intl.formatMessage({ id: 'PLEASE_WAIT_UNTIL_END' })}
            </VideoWarn>
          )}
          <ButtonContainer>
            <FormButton
              withLeftArrow
              variant="outlined"
              style={{ marginRight: '1em' }}
              onClick={handlePrevious}
            >
              {configuration?.generalInformation?.button_cancel}
            </FormButton>
            <FormButton
              withRightArrow
              type="primary"
              onClick={handleProcessStep}
              disabled={!isVideoWatched || loading}
            >
              {intl.formatMessage({ id: 'FINISH' })}
            </FormButton>
          </ButtonContainer>
        </>
      </VisitorsContainer>
    </Container>
  );
}

export default MultipleRegistrationBanner;
